import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Async thunks
export const fetchMultilingualStrings = createAsyncThunk(
  "multilingualString/fetchMultilingualStrings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/multilingual-strings");
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch multilingual strings"
      );
    }
  }
);

export const createMultilingualString = createAsyncThunk(
  "multilingualString/createMultilingualString",
  async (data, { rejectWithValue }) => {
    try {
      const response = await api.post("/admin/multilingual-strings", data);
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create multilingual string"
      );
    }
  }
);

export const updateMultilingualString = createAsyncThunk(
  "multilingualString/updateMultilingualString",
  async ({ key, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `/admin/multilingual-strings/${key}`,
        data
      );
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update multilingual string"
      );
    }
  }
);

export const deleteMultilingualString = createAsyncThunk(
  "multilingualString/deleteMultilingualString",
  async (key, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/multilingual-strings/${key}`);
      return key;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete multilingual string"
      );
    }
  }
);

export const fetchAvailableLanguages = createAsyncThunk(
  "multilingualString/fetchAvailableLanguages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/multilingual-strings/languages");
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch available languages"
      );
    }
  }
);

const multilingualStringSlice = createSlice({
  name: "multilingualString",
  initialState: {
    data: {},
    availableLanguages: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch multilingual strings
      .addCase(fetchMultilingualStrings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMultilingualStrings.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchMultilingualStrings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create multilingual string
      .addCase(createMultilingualString.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMultilingualString.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh data after creation
        // This will be handled by refetching in the component
      })
      .addCase(createMultilingualString.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update multilingual string
      .addCase(updateMultilingualString.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMultilingualString.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh data after update
        // This will be handled by refetching in the component
      })
      .addCase(updateMultilingualString.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete multilingual string
      .addCase(deleteMultilingualString.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMultilingualString.fulfilled, (state, action) => {
        state.loading = false;
        // Refresh data after deletion
        // This will be handled by refetching in the component
      })
      .addCase(deleteMultilingualString.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch available languages
      .addCase(fetchAvailableLanguages.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchAvailableLanguages.fulfilled, (state, action) => {
        state.availableLanguages = action.payload.languages || [];
      })
      .addCase(fetchAvailableLanguages.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError } = multilingualStringSlice.actions;
export default multilingualStringSlice.reducer;
